import { useContext, useEffect, useMemo, useState } from 'react';
import { TOGGLE_ACCESS_POLICIES_SANDBOX } from 'config/toggles';
import { SANDBOX_STORAGE_KEY, SandboxPermission } from 'modules/access-policy-sandbox/sandboxPermission';
import AuthenticationContext from '../auth/AuthenticationContext';
import { OrgContextModel } from '../components/OrgProvider';

function useSandboxPermissionsList(): SandboxPermission[] {
  const authContext = useContext(AuthenticationContext);
  const sandboxEnabled = authContext.isAdmin() && TOGGLE_ACCESS_POLICIES_SANDBOX;

  const [sandboxPermissions, setSandboxPermissions] = useState<SandboxPermission[]>(() =>
    sandboxEnabled ? JSON.parse(localStorage.getItem(SANDBOX_STORAGE_KEY) || '[]') : []
  );

  useEffect(() => {
    if (!sandboxEnabled) {
      localStorage.removeItem(SANDBOX_STORAGE_KEY);
      return;
    }

    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data.type === 'access-policy-sandbox-storage' && e.data.source === 'AccessPolicySandbox') {
        localStorage.setItem(e.data.key, e.data.value);
        setSandboxPermissions(e.data.value ? JSON.parse(e.data.value) : []);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sandboxEnabled]);

  return sandboxEnabled ? sandboxPermissions : [];
}

/**
 * True when the Access Policy Sandbox has at least one permission override configured.
 */
export function useHasActiveSandboxOverrides(): boolean {
  return useSandboxPermissionsList().length > 0;
}

/**
 * Patches hasPermission and hasSomePermission with overridden permissions from the
 * Access Policy Sandbox, allowing WCP Admins to explore Devsite without modifying actual
 * org entitlements. Returns the original context unchanged when the sandbox is inactive.
 */
export function useSandboxPermissions(context: OrgContextModel): OrgContextModel {
  const sandboxPermissions = useSandboxPermissionsList();

  return useMemo(() => {
    if (sandboxPermissions.length === 0) return context;

    return {
      ...context,
      hasPermission: (permission: string | string[]) => {
        const perms = typeof permission === 'string' ? [permission] : permission;
        return perms.every((p) => {
          const sim = sandboxPermissions.find((sp) => sp.permission === p);
          return sim !== undefined ? sim.enabled : context.hasPermission(p);
        });
      },
      hasSomePermission: (permission: string | string[]) => {
        const perms = typeof permission === 'string' ? [permission] : permission;
        return perms.some((p) => {
          const sim = sandboxPermissions.find((sp) => sp.permission === p);
          return sim !== undefined ? sim.enabled : context.hasSomePermission(p);
        });
      },
    };
  }, [context, sandboxPermissions]);
}
